import type { Recipe } from "@/types/models"
import {
  timeAgo,
  recipePreviewImage,
  recipeForks,
  recipeForkRating,
  formatAdminTimestamp,
} from "@/utils/helpers"

interface Props {
  recipe: Recipe
  large?: boolean
  adminMode?: boolean
}

export function RecipeCard({ recipe, large = false, adminMode = false }: Props) {
  const metaTime = timeAgo(recipe.publishedAt || recipe.discoveredAt)
  const sourceName = recipe.source?.name || recipe.source?.host
  const sourceInitial = (sourceName || "C").charAt(0).toUpperCase()

  if (large) {
    return (
      <article className="group overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
        <a
          href={recipe.url}
          target="_blank"
          rel="noopener"
          className="flex h-full flex-col"
        >
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-100">
            <img
              src={recipePreviewImage(recipe.previewImageUrl)}
              alt={recipe.title}
              loading="lazy"
              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
            />
          </div>
          <div className="flex flex-1 flex-col gap-4 p-6">
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <SourceBadge initial={sourceInitial} size="large" />
              <span className="font-medium text-slate-700">{sourceName}</span>
              {metaTime && (
                <>
                  <span className="text-slate-300">•</span>
                  <span>{metaTime}</span>
                </>
              )}
            </div>
            <h2 className="text-2xl font-semibold leading-tight tracking-tight text-slate-900 transition group-hover:text-slate-700 sm:text-3xl">
              {recipe.title}
            </h2>
            <div className="mt-auto flex items-center justify-between text-sm font-medium text-slate-600">
              <span
                className="text-base tracking-wider"
                title={`Rating: ${recipeForkRating(recipe.score)}/5`}
              >
                {recipeForks(recipe.score)}
              </span>
              <span className="inline-flex items-center gap-1 text-slate-400 transition group-hover:text-slate-500">
                <span>Open</span>
                <span aria-hidden="true">↗</span>
              </span>
            </div>
            {adminMode && <AdminDetails recipe={recipe} />}
          </div>
        </a>
      </article>
    )
  }

  return (
    <article className="group overflow-hidden rounded-3xl bg-white p-4 shadow-sm ring-1 ring-slate-200 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg sm:p-5">
      <a
        href={recipe.url}
        target="_blank"
        rel="noopener"
        className="flex h-full flex-col gap-4 sm:flex-row sm:items-stretch"
      >
        <div className="flex flex-1 flex-col gap-3">
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <SourceBadge initial={sourceInitial} />
            <span className="font-medium text-slate-700">{sourceName}</span>
            {metaTime && (
              <>
                <span className="text-slate-300">•</span>
                <span>{metaTime}</span>
              </>
            )}
          </div>
          <h3 className="text-lg font-semibold leading-snug text-slate-900 transition group-hover:text-slate-700 sm:text-xl">
            {recipe.title}
          </h3>
          <div className="mt-auto flex items-center gap-3 text-sm text-slate-500">
            <span
              className="text-base tracking-wider"
              title={`Rating: ${recipeForkRating(recipe.score)}/5`}
            >
              {recipeForks(recipe.score)}
            </span>
          </div>
          {adminMode && <AdminDetails recipe={recipe} compact />}
        </div>
        <div className="sm:w-44">
          <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-slate-100">
            <img
              src={recipePreviewImage(recipe.previewImageUrl)}
              alt={recipe.title}
              loading="lazy"
              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
            />
          </div>
        </div>
      </a>
    </article>
  )
}

function SourceBadge({ initial, size = "default" }: { initial: string; size?: "default" | "large" }) {
  const className =
    size === "large"
      ? "h-5 w-5 text-[10px]"
      : "h-4 w-4 text-[9px]"

  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-slate-200 font-semibold leading-none text-slate-600 ${className}`}
    >
      {initial}
    </span>
  )
}

function AdminDetails({ recipe, compact = false }: { recipe: Recipe; compact?: boolean }) {
  const padding = compact ? "mt-3 p-3" : "mt-4 p-4"

  return (
    <dl className={`space-y-1 rounded-2xl bg-slate-50 text-xs text-slate-500 ${padding}`}>
      <div className="flex items-center justify-between">
        <dt className="font-medium text-slate-600">Live score</dt>
        <dd className="text-slate-900">{recipe.score}</dd>
      </div>
      <div className="flex items-center justify-between">
        <dt className="font-medium text-slate-600">Pinterest saves</dt>
        <dd className="text-slate-900">{recipe.pinterestCount}</dd>
      </div>
      <div className="flex items-center justify-between">
        <dt className="font-medium text-slate-600">Published at</dt>
        <dd>{formatAdminTimestamp(recipe.publishedAt) || "n/a"}</dd>
      </div>
      <div className="flex items-center justify-between">
        <dt className="font-medium text-slate-600">Discovered at</dt>
        <dd>{formatAdminTimestamp(recipe.discoveredAt) || "n/a"}</dd>
      </div>
    </dl>
  )
}
